/**
 * Known webcams, as an overlay on the chart.
 *
 * **These are not chart features and must never be read as any.** Everything
 * else drawn on this chart comes from surveyed S-57 data with a known
 * provenance; a webcam is a third-party website at a position its own operator
 * published. A camera marker says "someone points a camera roughly here", not
 * "this position is surveyed" — which is why webcams live in their own module
 * with their own layer rather than being folded into the chart schema, and why
 * the panel that opens one says where the position came from.
 *
 * What they are genuinely good for: looking at a harbour before committing to
 * it. Sea state at the entrance, whether the wharf is full, whether the fog you
 * are in extends to where you are going.
 *
 * Positions locate the CAMERA, not its view. A lighthouse camera looking two
 * miles offshore is marked at the lighthouse.
 *
 * `NOVA_SCOTIA_WEBCAMS` is generated by `tools/fetch-webcams.mjs`; do not edit
 * it by hand. Cameras from anywhere else go in `ADDITIONAL_WEBCAMS` below.
 */

import { NOVA_SCOTIA_WEBCAMS } from './webcams.data';

export interface Webcam {
  id: string;
  name: string;
  /** Where the operator says it is, in words. Null when they did not say. */
  location: string | null;
  lat: number;
  lon: number;
  /** The page to open. Always external, always needs a network connection. */
  url: string;
  /** Who publishes it, shown so the skipper knows whose word the position is. */
  operator: string;
}

/**
 * Cameras that are not on novascotiawebcams.com.
 *
 * Sheet Harbour Marina publishes its own. Its position is the marina's own
 * published figure (N 44.918876° W 62.516070°, sheetharbourmarina.com), which
 * agrees with the MarinaSpots listing (44° 55.12' N, 62° 30.95' W) to about
 * fifteen metres — two independent sources rather than a guess off a map.
 * The marina sits on the East River between the bridges.
 */
const ADDITIONAL_WEBCAMS: readonly Webcam[] = [
  {
    id: 'sheet-harbour-marina',
    name: 'Sheet Harbour Marina',
    location: 'East River, Sheet Harbour',
    lat: 44.918876,
    lon: -62.516070,
    url: 'http://webcam.sheetharbourmarina.com/',
    operator: 'Sheet Harbour Marina',
  },
];

const NOVA_SCOTIA_WEBCAMS_BASE = 'https://www.novascotiawebcams.com/webcams/';

/** Every known webcam, sorted by name so the list is stable and browsable. */
export const WEBCAMS: readonly Webcam[] = [
  ...ADDITIONAL_WEBCAMS,
  ...NOVA_SCOTIA_WEBCAMS.map((camera) => ({
    id: camera.slug,
    name: camera.name,
    location: camera.location,
    lat: camera.lat,
    lon: camera.lon,
    url: `${NOVA_SCOTIA_WEBCAMS_BASE}${camera.slug}`,
    operator: 'novascotiawebcams.com',
  })),
].sort((a, b) => a.name.localeCompare(b.name));

/** Look one up by the id carried on a map feature. */
export function webcamById(id: string): Webcam | undefined {
  return WEBCAMS.find((camera) => camera.id === id);
}

export interface Bounds {
  south: number;
  west: number;
  north: number;
  east: number;
}

/**
 * Webcams inside a bounding box.
 *
 * Used for the "webcams near here" list rather than for drawing — the map draws
 * from the full set and lets MapLibre cull, which is both faster and correct
 * across the antimeridian, where this naive comparison would not be.
 */
export function webcamsWithin(bounds: Bounds, cameras: readonly Webcam[] = WEBCAMS): Webcam[] {
  return cameras.filter((camera) => camera.lat >= bounds.south
    && camera.lat <= bounds.north
    && camera.lon >= bounds.west
    && camera.lon <= bounds.east);
}

/** GeoJSON for the map layer. Only the id travels; the rest is looked up on tap. */
export function webcamFeatures(cameras: readonly Webcam[] = WEBCAMS): GeoJSON.Feature[] {
  return cameras.map((camera) => ({
    type: 'Feature' as const,
    geometry: { type: 'Point' as const, coordinates: [camera.lon, camera.lat] },
    properties: { webcamId: camera.id, name: camera.name },
  }));
}
